import { variables, Flex, FlexProps, Text, Dot } from '@bitrise/bitkit';
import { SeriesPointXY, Viz, VizSVG, VizSVGAxisX } from '@bitrise/bitviz';
import { SessionTrace } from '@trace/types';
import React, { PointerEvent, useMemo, FunctionComponent } from 'react';
import useTrackingEventTimed from '../../Tracking/useTrackingEventTimed';
import { formatterMs } from '../../../utils/metric/getFormattedValue';
import transition from '../../ViewVisualisations/transition';
import SessionVisualisationTracker from '../SessionVisualisationTracker';
import getOrderedSpans from './getOrderedSpans';
import { SIZE_GAP, SIZE_HEIGHT_EXPANDED } from './SessionTraceVisualisationSpan';
import SessionTraceVisualisationSpans from './SessionTraceVisualisationSpans';
import SessionTraceVisualisationStripes from './SessionTraceVisualisationStripes';

export const HEIGHT_MIN = 300;
export const PADDING_TOP = 24;

interface Props extends FlexProps {
  end: number;
  focusX: null | number;
  onFocusX: (x: null | number) => void;
  start: number;
  title?: string;
  traces: SessionTrace[];
}

const SessionTraceVisualisation: FunctionComponent<Props> = (props) => {
  const { end, focusX, onFocusX, start, title = 'User Journey', traces, ...rest } = props;
  const spans = useMemo(() => getOrderedSpans(traces), [traces]);
  const height = Math.max(HEIGHT_MIN, spans.length * (SIZE_HEIGHT_EXPANDED + SIZE_GAP));

  const handlePointerEnter = useTrackingEventTimed('SessionExplorer.Trace.Interaction', {});

  const handleSetFocus = (_: PointerEvent, point?: SeriesPointXY) => {
    if (point) {
      onFocusX(point.x);
    } else {
      onFocusX(null);
    }
  };

  return (
    <Flex {...rest} onPointerEnter={handlePointerEnter} onPointerLeave={handlePointerEnter.cancel}>
      <Flex
        alignChildrenHorizontal="between"
        alignChildrenVertical="end"
        direction="horizontal"
        gap="x3"
        margin="x4"
      >
        <Flex>
          <Text ellipsis>{title}</Text>
        </Flex>

        <Flex alignChildren="middle" direction="horizontal" gap="x4" grow>
          <Flex alignChildrenVertical="middle" direction="horizontal" gap="x2">
            <Flex>
              <Dot backgroundColor="green-2" size="0.75rem" />
            </Flex>
            <Flex>
              <Text config="8" textColor="gray-6">
                Interaction
              </Text>
            </Flex>
          </Flex>

          <Flex alignChildrenVertical="middle" direction="horizontal" gap="x2">
            <Flex>
              <Dot backgroundColor="blue-2" size="0.75rem" />
            </Flex>
            <Flex>
              <Text config="8" textColor="gray-6">
                View
              </Text>
            </Flex>
          </Flex>
        </Flex>
      </Flex>

      <Flex>
        <Viz
          height={height + PADDING_TOP * 2}
          onPointerLeave={handleSetFocus}
          onPointerMove={handleSetFocus}
          paddingTop={PADDING_TOP}
          transition={transition}
          xDomain={[start, end]}
          yDomain={[height, 0]}
        >
          <VizSVG>
            <VizSVGAxisX
              align="end"
              animate
              fill={variables.colorGray5}
              flip
              format={(x) => formatterMs(x - start)}
              stroke={variables.colorGray3}
              ticksOnly
            />

            <SessionTraceVisualisationStripes />
            <SessionTraceVisualisationSpans spans={spans} />
            <SessionVisualisationTracker x={focusX} />
          </VizSVG>
        </Viz>
      </Flex>
    </Flex>
  );
};

export default SessionTraceVisualisation;
